import {
  ResponsiveContainer,
  BarChart,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Area,
  Legend
} from 'recharts';
import { DSIcon, type DSIconName } from '@eiv2/design-system';
import { formatNumber } from '../services/format';
import type { NamedCount, TimeBucketRow } from '../services/dashboardInsights';

const COLORS = {
  brand: '#6A2ED3',
  brandSoft: '#D3C0FF',
  danger: '#CC3A3D',
  success: '#3DB34D',
  grid: '#E0E0E0',
  tick: '#8D8D8D'
} as const;

function EmptyChart({
  message,
  icon = 'Chart'
}: {
  message: string;
  icon?: DSIconName;
}) {
  return (
    <div className="flex h-64 flex-col items-center justify-center gap-3 px-4">
      <span
        aria-hidden
        className="inline-flex size-12 items-center justify-center rounded-2xl bg-gray-2 text-gray-5"
      >
        <DSIcon name={icon} size={24} />
      </span>
      <p className="max-w-sm text-center text-[13px] font-normal text-gray-5">{message}</p>
    </div>
  );
}

/** Barras horizontais — melhor para comparar categorias com rótulos longos (doc Recharts). */
export function AbandonmentByStepChart({ data }: { data: NamedCount[] }) {
  if (data.length === 0) {
    return (
      <EmptyChart
        icon="Status"
        message="Nenhum abandono com etapa registrada nesta amostra."
      />
    );
  }

  return (
    <div className="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 8, right: 24, left: 8, bottom: 0 }}
        >
          <CartesianGrid horizontal={false} stroke={COLORS.grid} strokeDasharray="3 3" />
          <XAxis allowDecimals={false} tick={{ fill: COLORS.tick, fontSize: 12 }} type="number" />
          <YAxis
            dataKey="label"
            tick={{ fill: COLORS.tick, fontSize: 12 }}
            type="category"
            width={110}
          />
          <Tooltip formatter={(value) => formatNumber(Number(value))} />
          <Bar dataKey="value" fill={COLORS.danger} name="Abandonos" maxBarSize={28} radius={[0, 8, 8, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * AreaChart — leitura de distribuição/volume ao longo dos buckets de tempo
 * (doc Recharts: Area enfatiza magnitude; Line enfatizaria só tendência).
 */
export function ActivityDurationChart({ data }: { data: TimeBucketRow[] }) {
  const hasData = data.some((row) => row.abandoned > 0 || row.converted > 0);
  if (!hasData) {
    return (
      <EmptyChart
        icon="Timer1"
        message="Sem durações calculáveis para abandonos ou conversões nesta amostra."
      />
    );
  }

  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fill: COLORS.tick, fontSize: 11 }} />
          <YAxis allowDecimals={false} tick={{ fill: COLORS.tick, fontSize: 12 }} width={36} />
          <Tooltip formatter={(value) => formatNumber(Number(value))} />
          <Legend />
          <Area
            dataKey="abandoned"
            fill={COLORS.danger}
            fillOpacity={0.28}
            name="Abandonos"
            stroke={COLORS.danger}
            strokeWidth={2}
            type="monotone"
          />
          <Area
            dataKey="converted"
            fill={COLORS.success}
            fillOpacity={0.28}
            name="Conversões"
            stroke={COLORS.success}
            strokeWidth={2}
            type="monotone"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
